import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import marketplaceService from '../../../services/marketplaceService';
import AuctionAlertSettings from '../../collection-dashboard/components/AuctionAlertSettings';

const ActiveAuctions = ({ onSelectAuction }) => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    auctionType: '',
    collectibleType: '',
    categoryId: ''
  });
  const [categories, setCategories] = useState([]);
  const [showAlertSettings, setShowAlertSettings] = useState(false);

  useEffect(() => {
    loadAuctions();
    loadCategories();
  }, []);

  const loadAuctions = async () => {
    setLoading(true);
    const { data, error } = await marketplaceService?.getActiveAuctions(filters);
    if (!error) {
      setAuctions(data || []);
    }
    setLoading(false);
  };

  const loadCategories = async () => {
    const { data } = await marketplaceService?.getAuctionCategories();
    setCategories(data || []);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadAuctions();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      auctionType: '',
      collectibleType: '',
      categoryId: ''
    });
    setTimeout(() => loadAuctions(), 100);
  };

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;

    if (diff <= 0) return 'Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
        {[1, 2, 3, 4, 5, 6]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-lg h-96" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            placeholder="Search auctions..."
            value={filters?.search}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
          />
          <Select
            value={filters?.auctionType}
            onChange={(e) => handleFilterChange('auctionType', e?.target?.value)}
          >
            <option value="">All Auction Types</option>
            <option value="regular">Regular Auctions</option>
            <option value="premiere">Premiere Auctions</option>
          </Select>
          <Select
            value={filters?.collectibleType}
            onChange={(e) => handleFilterChange('collectibleType', e?.target?.value)}
          >
            <option value="">All Types</option>
            <option value="vintage-watches">Vintage Watches</option>
            <option value="rare-coins">Rare Coins</option>
            <option value="sports-cards">Sports Cards</option>
            <option value="comics">Comics</option>
            <option value="books">First Editions</option>
          </Select>
          <Select
            value={filters?.categoryId}
            onChange={(e) => handleFilterChange('categoryId', e?.target?.value)}
          >
            <option value="">All Categories</option>
            {categories?.map(cat => (
              <option key={cat?.id} value={cat?.id}>
                {cat?.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex space-x-3">
            <Button onClick={applyFilters} iconName="Search" iconPosition="left">
              Apply Filters
            </Button>
            <Button onClick={clearFilters} variant="outline" iconName="X" iconPosition="left">
              Clear
            </Button>
          </div>
          <Button
            onClick={() => setShowAlertSettings(!showAlertSettings)}
            variant={showAlertSettings ? 'primary' : 'outline'}
            iconName="Bell"
            iconPosition="left"
          >
            Alert Settings
          </Button>
        </div>
      </div>

      {/* Auction Alert Settings Panel */}
      {showAlertSettings && (
        <AuctionAlertSettings onClose={() => setShowAlertSettings(false)} />
      )}

      {/* Auctions Grid */}
      {auctions?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Gavel" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No auctions found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or check back later</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {auctions?.map(auction => (
            <div
              key={auction?.id}
              className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => onSelectAuction(auction)}
            >
              {/* Image */}
              <div className="aspect-square bg-muted relative">
                {auction?.featured_image_url || auction?.collectible?.primary_image_url ? (
                  <img
                    src={auction?.featured_image_url || auction?.collectible?.primary_image_url}
                    alt={auction?.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={48} className="text-muted-foreground" />
                  </div>
                )}
                {auction?.auction_type === 'premiere' && (
                  <div className="absolute top-3 left-3 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                    <Icon name="Star" size={12} />
                    <span>Premiere</span>
                  </div>
                )}
                <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-xs font-medium">
                  {getTimeRemaining(auction?.end_date)}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                  {auction?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {auction?.description}
                </p>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Current Bid:</span>
                    <span className="font-bold text-primary text-lg">
                      ${parseFloat(auction?.current_bid || auction?.starting_bid || 0)?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Bids:</span>
                    <span className="font-medium text-foreground">
                      {auction?.bid_count || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Condition:</span>
                    <span className="font-medium text-foreground capitalize">
                      {auction?.condition?.replace('-', ' ')}
                    </span>
                  </div>
                </div>

                {/* Action */}
                <Button size="sm" iconName="Gavel" iconPosition="left" fullWidth>
                  Place Bid
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveAuctions;